import { IconArrowLeft, IconArrowRight } from '@tabler/icons-preact';
import { type ComponentChild, type ComponentChildren, h } from 'preact';
import { useMemo } from 'preact/hooks';

import { Link } from '../components/Link.tsx';
import { NotFound } from '../components/NotFound.tsx';
import { PageLayout } from '../components/PageLayout.tsx';
import { usePostIndex } from '../hooks/usePostIndex.ts';
import css from './BlogPostPage.module.css';

interface Props {
  readonly postId: string;
}

export const BlogPostPage = ({ postId }: Props) => {
  const index = usePostIndex();
  const [post, prevPostId, nextPostId] = useMemo(() => {
    if (!index.value) return [undefined, undefined, undefined] as const;
    const i = index.value.posts.findIndex((post) => post.id === postId);
    const post = index.value.posts[i];
    const prevPostId = index.value.posts[i + 1]?.id;
    const nextPostId = index.value.posts[i - 1]?.id;
    return [post, prevPostId, nextPostId] as const;
  }, [index, postId]);

  const content = useMemo(() => {
    return post ? parseHTML(post.html) : null;
  }, [post]);

  return (
    <PageLayout
      title={['Blog', post?.title]}
      mainClass={css.main}
      nav={[
        <Link href="/blog" variant="nav">
          Blog
        </Link>,
        post && (
          <Link href={`/blog/${postId}`} variant="nav">
            {post.title}
          </Link>
        ),
      ]}
    >
      <header class={css.header}>
        {(prevPostId || nextPostId) && (
          <div class={css.headerButtons}>
            {prevPostId && (
              <Link class={css.backLink} href={`/blog/${prevPostId}`} variant="nav">
                <IconArrowLeft /> Previous
              </Link>
            )}
            {nextPostId && (
              <Link class={css.nextLink} href={`/blog/${nextPostId}`} variant="nav">
                Next <IconArrowRight />
              </Link>
            )}
          </div>
        )}
        {post && (
          <dl class={css.headerInfo}>
            <dt>Created:</dt>
            <dd>{new Date(post.created).toLocaleString()}</dd>
            {post.updated && (
              <>
                <dt>Updated:</dt>
                <dd>{new Date(post.updated).toLocaleString()}</dd>
              </>
            )}
            {post.summary && (
              <>
                <dt>Summary:</dt>
                <dd>{post.summary}</dd>
              </>
            )}
          </dl>
        )}
      </header>
      <article class={css.post}>{!post && index.state === 'error' ? <NotFound /> : content}</article>
    </PageLayout>
  );
};

function parseHTML(html: string): ComponentChildren {
  const template = document.createElement('template');
  template.innerHTML = html;
  return [...template.content.childNodes].map(toVNode);
}

function toVNode(node: Node): ComponentChild {
  if (node instanceof Text) return node.data; // Text nodes --> strings
  if (!(node instanceof HTMLElement)) return null; // Ignore non-HTMLElements

  if (node instanceof HTMLAnchorElement) {
    const href = node.getAttribute('href');

    if (href) {
      return h(Link, {
        href,
        variant: isLinkedHeading(node) ? 'nav' : 'underline',
        children: [...node.childNodes].map(toVNode),
      });
    }
  }

  const props = Object.fromEntries([...node.attributes].map((attr) => [attr.name, attr.value]));
  return h(node.localName, { ...props, children: [...node.childNodes].map(toVNode) });
}

function isLinkedHeading(node: HTMLAnchorElement): boolean {
  if (node.parentElement?.childElementCount === 1 && /^h[1-6]$/u.test(node.parentElement.localName)) {
    return true;
  }

  if (
    node.childElementCount === 1 &&
    node.firstElementChild != null &&
    /^h[1-6]$/u.test(node.firstElementChild.localName)
  ) {
    return true;
  }

  return false;
}
